// Meta Pixel Service for MockTrail Frontend
// Handles Meta Pixel tracking with UTM parameters and unique identifiers

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

interface MetaPixelEvent {
  eventName: string;
  parameters?: Record<string, any>;
  value?: number;
  currency?: string;
}

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

class MetaPixelService {
  private isEnabled: boolean = false;
  private pixelId: string | null = null;

  constructor() {
    this.pixelId = process.env.REACT_APP_META_PIXEL_ID || null;
    this.isEnabled = !!this.pixelId && this.pixelId.length >= 10;
    
    if (this.isEnabled) {
      this.initializePixel();
    }
  }

  private initializePixel(): void {
    try {
      // Meta Pixel Base Code
      const f = window as any;
      const b = document;
      const e = 'script';
      const v = 'https://connect.facebook.net/en_US/fbevents.js';
      
      if (f.fbq) return;
      
      // Create fbq function with proper typing
      const n = f.fbq = function() {
        (n as any).callMethod ? (n as any).callMethod.apply(n, arguments) : (n as any).queue.push(arguments);
      };
      
      if (!f._fbq) f._fbq = n;
      (n as any).push = n;
      (n as any).loaded = true;
      (n as any).version = '2.0';
      (n as any).queue = [];
      
      const t = b.createElement(e);
      t.async = true;
      t.src = v;
      
      const s = b.getElementsByTagName(e)[0];
      s.parentNode?.insertBefore(t, s);

      // Initialize with Pixel ID
      window.fbq('init', this.pixelId);
      window.fbq('track', 'PageView');

      console.log('Meta Pixel initialized successfully with ID:', this.pixelId?.substring(0, 6) + '...');
    } catch (error) {
      console.error('Meta Pixel initialization error:', error);
      this.isEnabled = false;
    }
  }

  /**
   * Extract UTM parameters from URL
   */
  private extractUTMParams(): UTMParams {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: UTMParams = {};
    
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utmParams[param as keyof UTMParams] = value;
      }
    });
    
    return utmParams;
  }

  /**
   * Get stored UTM parameters
   */
  private getStoredUTMParams(): UTMParams {
    try {
      const stored = sessionStorage.getItem('utm_params');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  /**
   * Store UTM parameters
   */
  private storeUTMParams(params: UTMParams): void {
    try {
      if (Object.keys(params).length > 0) {
        sessionStorage.setItem('utm_params', JSON.stringify(params));
      }
    } catch (error) {
      console.error('Failed to store UTM params:', error);
    }
  }

  /**
   * Initialize UTM tracking
   */
  public initializeUTMTracking(): void {
    const currentUTM = this.extractUTMParams();
    if (Object.keys(currentUTM).length > 0) {
      this.storeUTMParams(currentUTM);
    }
  }

  /**
   * Generate unique identifier
   */
  private generateUniqueId(): string {
    return `mt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Track an event
   */
  public trackEvent(event: MetaPixelEvent): void {
    if (!this.isEnabled) {
      console.warn('Meta Pixel is not enabled');
      return;
    }

    try {
      const { eventName, parameters, value, currency } = event;
      const utmParams = this.getStoredUTMParams();
      const uniqueId = this.generateUniqueId();
      
      const eventParams = {
        ...parameters,
        ...utmParams,
        unique_id: uniqueId,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname
      };
      
      // Handle custom events differently
      if (eventName === 'CustomEvent' && parameters?.event_name) {
        // For custom events, use the event_name parameter as the custom event name
        const customEventName = parameters.event_name;
        const customParameters = { ...eventParams };
        delete (customParameters as any).event_name; // Remove event_name from parameters
        
        if (value !== undefined && currency) {
          window.fbq('trackCustom', customEventName, {
            ...customParameters,
            value,
            currency
          });
        } else {
          window.fbq('trackCustom', customEventName, customParameters);
        }
      } else {
        // For standard events
        if (value !== undefined && currency) {
          window.fbq('track', eventName, {
            ...eventParams,
            value,
            currency
          });
        } else {
          window.fbq('track', eventName, eventParams);
        }
      }

      console.log('Meta Pixel event tracked:', eventName, eventParams);
    } catch (error) {
      console.error('Error tracking Meta Pixel event:', error);
    }
  }

  /**
   * Track popup blocking events
   */
  public trackPopupBlocked(authMethod: 'google' | 'jobprot', location: string, additionalParams?: Record<string, any>): void {
    const utmParams = this.getStoredUTMParams();
    const uniqueId = this.generateUniqueId();
    
    this.trackEvent({
      eventName: 'CustomEvent',
      parameters: {
        event_name: 'PopupBlocked',
        auth_method: authMethod,
        location: location,
        unique_id: uniqueId,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        user_agent: navigator.userAgent,
        browser: this.detectBrowser(),
        device_type: this.detectDeviceType(),
        ...utmParams,
        ...additionalParams
      }
    });

    // Also track as a standard Meta Pixel event for better analytics
    this.trackEvent({
      eventName: 'CustomEvent',
      parameters: {
        event_name: 'AuthenticationError',
        error_type: 'popup_blocked',
        auth_method: authMethod,
        location: location,
        unique_id: uniqueId,
        ...utmParams,
        ...additionalParams
      }
    });
  }

  /**
   * Track authentication attempts
   */
  public trackAuthAttempt(authMethod: 'google' | 'jobprot', location: string, additionalParams?: Record<string, any>): void {
    const utmParams = this.getStoredUTMParams();
    const uniqueId = this.generateUniqueId();
    
    this.trackEvent({
      eventName: 'CustomEvent',
      parameters: {
        event_name: 'AuthAttempt',
        auth_method: authMethod,
        location: location,
        unique_id: uniqueId,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        user_agent: navigator.userAgent,
        browser: this.detectBrowser(),
        device_type: this.detectDeviceType(),
        ...utmParams,
        ...additionalParams
      }
    });
  }

  /**
   * Track successful authentication
   */
  public trackAuthSuccess(authMethod: 'google' | 'jobprot', location: string, additionalParams?: Record<string, any>): void {
    const utmParams = this.getStoredUTMParams();
    const uniqueId = this.generateUniqueId();
    
    this.trackEvent({
      eventName: 'CompleteRegistration',
      parameters: {
        auth_method: authMethod,
        location: location,
        unique_id: uniqueId,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        user_agent: navigator.userAgent,
        browser: this.detectBrowser(),
        device_type: this.detectDeviceType(),
        ...utmParams,
        ...additionalParams
      }
    });
  }

  /**
   * Track authentication failures
   */
  public trackAuthFailure(authMethod: 'google' | 'jobprot', location: string, errorType: string, errorMessage: string, additionalParams?: Record<string, any>): void {
    const utmParams = this.getStoredUTMParams();
    const uniqueId = this.generateUniqueId();
    
    this.trackEvent({
      eventName: 'CustomEvent',
      parameters: {
        event_name: 'AuthFailure',
        auth_method: authMethod,
        location: location,
        error_type: errorType,
        error_message: errorMessage,
        unique_id: uniqueId,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        user_agent: navigator.userAgent,
        browser: this.detectBrowser(),
        device_type: this.detectDeviceType(),
        ...utmParams,
        ...additionalParams
      }
    });
  }

  /**
   * Detect browser type
   */
  private detectBrowser(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    return 'Unknown';
  }

  /**
   * Detect device type
   */
  private detectDeviceType(): string {
    const userAgent = navigator.userAgent;
    if (/Android/i.test(userAgent)) return 'Android';
    if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
    if (/Windows/i.test(userAgent)) return 'Windows';
    if (/Mac/i.test(userAgent)) return 'Mac';
    if (/Linux/i.test(userAgent)) return 'Linux';
    return 'Unknown';
  }

  /**
   * Check if pixel is enabled
   */
  public isPixelEnabled(): boolean {
    return this.isEnabled;
  }
}

export const metaPixelService = new MetaPixelService(); 